import { motion, useReducedMotion } from 'motion/react'
import { BookMeButton } from '../components/BookingGuide'
import { MotionMarquee } from '../components/MotionMarquee'
import { Page } from '../components/Page'
import { ProjectGraphic } from '../components/ProjectGraphic'
import { Reveal } from '../components/Reveal'
import { Link } from '../lib/router'
import type { PortfolioContent, SocialLinks } from '../types'

const footerContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.11,
    },
  },
}

const footerLinkVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.68, ease: [0.22, 1, 0.36, 1] as const },
  },
}

function HomeFooter({ socialLinks }: { socialLinks: SocialLinks }) {
  const reduceMotion = useReducedMotion()

  return (
    <motion.footer
      className="home-footer"
      initial={reduceMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.7 }}
      variants={footerContainerVariants}
    >
      <nav className="home-footer-nav" aria-label="Footer navigation">
        <motion.span variants={footerLinkVariants}>
          <Link className="home-footer-link" to="/about">About Me</Link>
        </motion.span>
        {socialLinks.linkedin && (
          <motion.span variants={footerLinkVariants}>
            <a className="home-footer-link" href={socialLinks.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
          </motion.span>
        )}
        {socialLinks.github && (
          <motion.span variants={footerLinkVariants}>
            <a className="home-footer-link" href={socialLinks.github} target="_blank" rel="noreferrer">GitHub</a>
          </motion.span>
        )}
      </nav>
    </motion.footer>
  )
}

export function HomePage({ content }: { content: PortfolioContent }) {
  const firstProject = content.projects[0]
  const [accentLead, ...accentRest] = content.profile.headlineAccent.split(' ')

  return (
    <Page className="home-page">
      <section className="hero-section">
        <div className="hero-copy">
          <Reveal delay={0.05}>
            <p className="identity-line">
              <strong>{content.profile.name}</strong>
              <span className="identity-rule" aria-hidden="true" />
              <span className="identity-role">{content.profile.role}</span>
            </p>
          </Reveal>
          <h1>
            <Reveal delay={0.12}>{content.profile.headlineLead}</Reveal>
            <Reveal delay={0.2} className="solution-line">
              <span>{accentLead}{accentRest.length > 0 && <><br className="mobile-accent-break" /> {accentRest.join(' ')}</>}</span>
            </Reveal>
          </h1>
          <Reveal delay={0.28} className="hero-positioning-reveal">
            <p className="hero-positioning-statement">
              <strong>No generic sales pitches. No empty marketing promises.</strong>
              <strong>Just tools and strategies that help your team and business build.</strong>
            </p>
          </Reveal>
        </div>
        <BookMeButton className="hero-book" />
        <div className="scroll-note" aria-hidden="true"><span>Scroll to explore</span><i /></div>
      </section>

      {firstProject && (
        <section className="home-work-preview">
          <MotionMarquee label="Recent Projects" className="home-work-marquee" />
          <motion.div initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.16 }} transition={{ duration: 0.68, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/work" className="featured-project-link" aria-label={`View ${firstProject.title} and more work`}>
              <div className="featured-project-media">
                <ProjectGraphic
                  src={firstProject.mediaPath}
                  alt="Twilabeth connections for phone, chat, company workspaces, APIs, and website integrations"
                  animated={firstProject.title.toLowerCase() === 'twilabeth'}
                />
              </div>
              <div className="featured-project-copy">
                <p>{firstProject.category}</p><h2>{firstProject.title}</h2><span>View all work&nbsp; ↗</span>
              </div>
            </Link>
          </motion.div>
        </section>
      )}
      <HomeFooter socialLinks={content.socialLinks} />
    </Page>
  )
}
